import { useState, useRef, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useAuth } from '../hooks/useAuth'

interface FileItem {
  name: string
  path: string
  type: 'file' | 'folder'
  size?: number
  updated?: string
}

interface Storage {
  id: string
  storage_id: string
  name: string
  description: string | null
  backend_type: string
  is_active: boolean
}

interface Branch {
  name: string
  protected: boolean
  is_default: boolean
}

interface Commit {
  sha: string
  short_sha: string
  message: string
  author: {
    name: string
    email: string
    date: string
  }
  url: string
}

function formatSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export function Files() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const currentPath = searchParams.get('path') || '/'
  const storageParam = searchParams.get('storage')

  const [selectedStorageId, setSelectedStorageId] = useState<string | null>(storageParam)
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<FileItem | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<string | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string | null>(null)
  const [showCommits, setShowCommits] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Check if user has write access (admin or partner)
  const canWrite = user?.role === 'admin' || user?.role === 'partner'

  // Fetch available storages for this user
  const { data: storages, isLoading: storagesLoading } = useQuery({
    queryKey: ['storages'],
    queryFn: () => api.get<Storage[]>('/storages'),
  })

  // Derive selectedStorage and isGitHub early for use in queries
  const selectedStorage = storages?.find(s => s.id === selectedStorageId)
  const isGitHub = selectedStorage?.backend_type === 'github'

  // For GitHub, extract repo from path (first segment after root)
  // Path like /my-repo/src/file.py -> repo = 'my-repo', pathInRepo = 'src/file.py'
  const pathParts = currentPath.split('/').filter(Boolean)
  const currentRepo = isGitHub && pathParts.length > 0 ? pathParts[0] : null
  const pathInRepo = isGitHub && pathParts.length > 1 ? pathParts.slice(1).join('/') : ''
  const isInsideRepo = isGitHub && currentRepo !== null

  // Auto-select first storage if none selected
  useEffect(() => {
    if (storages && storages.length > 0 && !selectedStorageId) {
      const firstStorage = storages[0]
      setSelectedStorageId(firstStorage.id)
      // Update URL
      const newParams = new URLSearchParams(searchParams)
      newParams.set('storage', firstStorage.id)
      setSearchParams(newParams, { replace: true })
    }
  }, [storages, selectedStorageId, searchParams, setSearchParams])

  const handleStorageChange = (storageId: string) => {
    setSelectedStorageId(storageId)
    setSelectedBranch(null) // Reset branch when switching storage
    // Reset to root path when switching storage
    const newParams = new URLSearchParams()
    newParams.set('storage', storageId)
    newParams.set('path', '/')
    setSearchParams(newParams)
  }

  const { data: files, isLoading, error } = useQuery({
    queryKey: ['files', selectedStorageId, currentPath, selectedBranch],
    queryFn: () => api.get<FileItem[]>('/files', {
      storage: selectedStorageId!,
      path: currentPath,
      ...(selectedBranch && { branch: selectedBranch })
    }),
    enabled: !!selectedStorageId,
  })

  // Fetch branches for GitHub storages (only when inside a repo)
  const { data: branches } = useQuery({
    queryKey: ['branches', selectedStorageId, currentRepo],
    queryFn: () => api.get<{ branches: Branch[] }>('/files/branches', {
      storage: selectedStorageId!,
      repo: currentRepo!
    }),
    enabled: !!selectedStorageId && isInsideRepo && !!currentRepo,
  })

  // Fetch commits for the current path (GitHub only, when inside a repo)
  const { data: commits } = useQuery({
    queryKey: ['commits', selectedStorageId, currentRepo, pathInRepo, selectedBranch],
    queryFn: () => api.get<{ commits: Commit[] }>('/files/commits', {
      storage: selectedStorageId!,
      repo: currentRepo!,
      path: pathInRepo,
      ...(selectedBranch && { branch: selectedBranch })
    }),
    enabled: !!selectedStorageId && isInsideRepo && !!currentRepo && showCommits,
  })

  // Reset branch when repo changes, and initialize when branches load
  useEffect(() => {
    if (branches?.branches && branches.branches.length > 0) {
      const defaultBranch = branches.branches.find(b => b.is_default) || branches.branches[0]
      // Reset to default branch if current selection isn't in the list (repo changed)
      if (!selectedBranch || !branches.branches.some(b => b.name === selectedBranch)) {
        setSelectedBranch(defaultBranch.name)
      }
    }
  }, [branches, currentRepo]) // Depend on currentRepo to reset on repo change

  const createFolderMutation = useMutation({
    mutationFn: (name: string) =>
      api.post<FileItem>('/files/directory', { storage: selectedStorageId, path: currentPath, name }),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['files', selectedStorageId, currentPath] })
      setShowNewFolder(false)
      setNewFolderName('')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (path: string) =>
      api.delete<{ deleted: number }>(`/files?storage=${selectedStorageId}&path=${encodeURIComponent(path)}`),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['files', selectedStorageId, currentPath] })
      setDeleteTarget(null)
    },
  })

  const handleNavigate = (item: FileItem) => {
    if (item.type === 'folder') {
      const params = new URLSearchParams()
      if (selectedStorageId) params.set('storage', selectedStorageId)
      params.set('path', item.path)
      navigate(`/files?${params.toString()}`)
    }
  }

  const handleDownload = async (item: FileItem) => {
    try {
      const response = await api.get<{ url: string }>('/files/download', {
        storage: selectedStorageId!,
        path: item.path,
      })
      window.open(response.url, '_blank')
    } catch (err) {
      console.error('Download failed:', err)
    }
  }

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file || !selectedStorageId) return

    setUploading(true)
    setUploadProgress(`Uploading ${file.name}...`)

    try {
      // Get signed upload URL
      const { url, contentType, path: filePath } = await api.post<{
        url: string
        path: string
        contentType: string
      }>('/files/upload-url', {
        storage: selectedStorageId,
        path: currentPath,
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
      })

      // Check if this is a Google Drive or GitHub upload (special URL schemes)
      if (url.startsWith('gdrive://') || url.startsWith('github://')) {
        // These backends don't support pre-signed URLs
        // Upload through our API instead
        const formData = new FormData()
        formData.append('storage', selectedStorageId)
        formData.append('path', filePath)
        formData.append('file', file)
        if (selectedBranch) {
          formData.append('branch', selectedBranch)
        }

        await api.uploadFile('/files/upload', formData)
      } else {
        // Upload directly to storage (GCS, etc.)
        await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': contentType,
          },
          body: file,
        })
      }

      // Refresh file list
      queryClient.refetchQueries({ queryKey: ['files', selectedStorageId, currentPath] })
      setUploadProgress(null)
    } catch (err) {
      console.error('Upload failed:', err)
      setUploadProgress('Upload failed')
      setTimeout(() => setUploadProgress(null), 3000)
    } finally {
      setUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return
    createFolderMutation.mutate(newFolderName.trim())
  }

  const handleDelete = () => {
    if (!deleteTarget) return
    deleteMutation.mutate(deleteTarget.path)
  }

  const goBack = () => {
    const parts = currentPath.split('/').filter(Boolean)
    parts.pop()
    const parentPath = parts.length > 0 ? `/${parts.join('/')}/` : '/'
    const params = new URLSearchParams()
    if (selectedStorageId) params.set('storage', selectedStorageId)
    params.set('path', parentPath)
    navigate(`/files?${params.toString()}`)
  }

  const navigateToPath = (path: string) => {
    const params = new URLSearchParams()
    if (selectedStorageId) params.set('storage', selectedStorageId)
    params.set('path', path)
    navigate(`/files?${params.toString()}`)
  }

  // Show loading while storages are loading
  if (storagesLoading) {
    return <div className="text-[var(--color-text-secondary)]">Loading...</div>
  }

  // Show message if no storages available
  if (!storages || storages.length === 0) {
    return (
      <div className="max-w-4xl">
        <h1 className="text-3xl mb-8">Files</h1>
        <div className="border border-[var(--color-border)] rounded-lg p-6 text-center">
          <p className="text-[var(--color-text-secondary)] mb-2">
            No storage backends available.
          </p>
          <p className="text-sm text-[var(--color-text-tertiary)]">
            Contact an administrator to get storage access.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl">
      {/* Storage and branch selectors */}
      <div className="mb-6 flex items-center gap-6 flex-wrap">
        {/* Storage selector - only show if multiple storages */}
        {storages.length > 1 && (
          <div className="flex items-center gap-3">
            <label className="text-sm text-[var(--color-text-secondary)]">Storage:</label>
            <select
              value={selectedStorageId || ''}
              onChange={(e) => handleStorageChange(e.target.value)}
              className="input py-1 px-2"
            >
              {storages.map((storage) => (
                <option key={storage.id} value={storage.id}>
                  {storage.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Branch selector - only show for GitHub storages when inside a repo */}
        {isInsideRepo && branches?.branches && branches.branches.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="text-sm text-[var(--color-text-secondary)]">Branch:</label>
            <select
              value={selectedBranch || ''}
              onChange={(e) => setSelectedBranch(e.target.value)}
              className="input py-1 px-2"
            >
              {branches.branches.map((branch) => (
                <option key={branch.name} value={branch.name}>
                  {branch.name}{branch.is_default ? ' (default)' : ''}{branch.protected ? ' 🔒' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Commit history toggle - only show for GitHub storages when inside a repo */}
        {isInsideRepo && (
          <button
            onClick={() => setShowCommits(!showCommits)}
            className={`text-sm px-3 py-1 rounded border ${
              showCommits
                ? 'bg-[var(--color-bg-secondary)] border-[var(--color-border)]'
                : 'border-transparent hover:border-[var(--color-border)]'
            }`}
          >
            {showCommits ? 'Hide Commits' : 'Show Commits'}
          </button>
        )}
      </div>

      {/* Header with actions */}
      <div className="flex items-center justify-between mb-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm">
          {storages.length === 1 && selectedStorage && (
            <>
              <span className="text-[var(--color-text-secondary)]">{selectedStorage.name}</span>
              <span className="text-[var(--color-text-tertiary)]">/</span>
            </>
          )}
          <button
            onClick={() => navigateToPath('/')}
            className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
          >
            {storages.length > 1 ? 'Files' : 'Root'}
          </button>
          {pathParts.map((part, i) => (
            <span key={i} className="flex items-center gap-2">
              <span className="text-[var(--color-text-tertiary)]">/</span>
              <button
                onClick={() => {
                  const path = '/' + pathParts.slice(0, i + 1).join('/') + '/'
                  navigateToPath(path)
                }}
                className="text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
              >
                {part}
              </button>
            </span>
          ))}
        </div>

        {/* Actions */}
        {canWrite && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowNewFolder(true)}
              className="btn btn-secondary text-sm"
            >
              New Folder
            </button>
            <label className="btn btn-primary text-sm cursor-pointer">
              Upload File
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleUpload}
                disabled={uploading}
              />
            </label>
          </div>
        )}
      </div>

      {/* Upload progress */}
      {uploadProgress && (
        <div className="mb-4 px-4 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded text-sm">
          {uploadProgress}
        </div>
      )}

      {/* Commit history panel - only for GitHub storages when inside a repo */}
      {isInsideRepo && showCommits && (
        <div className="mb-6 border border-[var(--color-border)] rounded-lg overflow-hidden">
          <div className="bg-[var(--color-bg-secondary)] px-4 py-2 border-b border-[var(--color-border)]">
            <h3 className="text-sm font-medium">Recent Commits</h3>
          </div>
          {commits?.commits && commits.commits.length > 0 ? (
            <div className="divide-y divide-[var(--color-border)] max-h-64 overflow-y-auto">
              {commits.commits.map((commit) => (
                <div key={commit.sha} className="px-4 py-3 hover:bg-[var(--color-bg-secondary)]/50">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm truncate">{commit.message.split('\n')[0]}</p>
                      <p className="text-xs text-[var(--color-text-tertiary)] mt-1">
                        {commit.author.name} • {new Date(commit.author.date).toLocaleDateString()}
                      </p>
                    </div>
                    <code className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-bg-secondary)] px-2 py-1 rounded">
                      {commit.short_sha}
                    </code>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-3 text-sm text-[var(--color-text-secondary)]">
              No commits found for this path.
            </div>
          )}
        </div>
      )}

      {/* Back button */}
      {currentPath !== '/' && (
        <button
          onClick={goBack}
          className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] mb-4"
        >
          ← Back
        </button>
      )}

      {/* File list */}
      {isLoading ? (
        <p className="text-[var(--color-text-secondary)]">Loading...</p>
      ) : error ? (
        <div className="border border-[var(--color-border)] rounded-lg p-6 text-center">
          <p className="text-[var(--color-text-secondary)] mb-2">
            {(error as Error).message?.includes('403') || (error as Error).message?.includes('Access')
              ? 'You do not have access to view files in this location.'
              : 'Failed to load files'}
          </p>
          <p className="text-sm text-[var(--color-text-tertiary)]">
            Contact an administrator if you need access.
          </p>
        </div>
      ) : files && files.length > 0 ? (
        <div className="border border-[var(--color-border)] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[var(--color-bg-secondary)]">
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-right px-4 py-2 w-24">Size</th>
                <th className="text-right px-4 py-2 w-40">Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((item) => (
                <tr
                  key={item.path}
                  className="border-t border-[var(--color-border)] hover:bg-[var(--color-bg-secondary)]/50"
                >
                  <td className="px-4 py-3">
                    {item.type === 'folder' ? (
                      <button
                        onClick={() => handleNavigate(item)}
                        className="flex items-center gap-2 text-[var(--color-text)] hover:underline"
                      >
                        <span>📁</span>
                        <span>{item.name}</span>
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span>📄</span>
                        <span>{item.name}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-[var(--color-text-secondary)]">
                    {formatSize(item.size)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      {item.type === 'file' && (
                        <button
                          onClick={() => handleDownload(item)}
                          className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)]"
                        >
                          Download
                        </button>
                      )}
                      {canWrite && !isGitHub && (
                        <button
                          onClick={() => setDeleteTarget(item)}
                          className="text-sm text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-[var(--color-text-secondary)]">This folder is empty.</p>
      )}

      {/* New Folder Modal */}
      {showNewFolder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-medium mb-4">New Folder</h3>
            <input
              type="text"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="input mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateFolder()
                if (e.key === 'Escape') setShowNewFolder(false)
              }}
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowNewFolder(false)
                  setNewFolderName('')
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim() || createFolderMutation.isPending}
                className="btn btn-primary"
              >
                {createFolderMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
            {createFolderMutation.isError && (
              <p className="mt-2 text-sm text-red-400">Failed to create folder</p>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg p-6 w-full max-w-sm">
            <h3 className="text-lg font-medium mb-2">Delete {deleteTarget.type}?</h3>
            <p className="text-[var(--color-text-secondary)] mb-4">
              Are you sure you want to delete "{deleteTarget.name}"?
              {deleteTarget.type === 'folder' && ' All contents will be deleted.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="btn bg-red-600 hover:bg-red-700 text-white"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
            {deleteMutation.isError && (
              <p className="mt-2 text-sm text-red-400">Failed to delete</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
